import React from "react";
import { BookOpen, Palette, Wind, Music } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pathways = [{
  title: "Storytelling",
  description: "We guide children and families to find their voice, helping them articulate their experiences and reframe their narratives with courage and hope.",
  icon: BookOpen
}, {
  title: "Art",
  description: "Through painting, drawing, and visual creation, we provide a safe canvas for emotions to flow, allowing for expression when words are not enough.",
  icon: Palette
}, {
  title: "Movement",
  description: "Our gentle somatic practices and movement workshops help release tension, fostering a deep sense of grounding, connection, and bodily peace.",
  icon: Wind
}, {
  title: "Music",
  description: "Through rhythm and melody, we invite children to explore their inner world, finding harmony and strength in the universal language of sound.",
  icon: Music
}];
export default function OurCreativePathways() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-creative-pathways" ref={ref} className="relative w-full py-24 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-[var(--chart-3)]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-[var(--ring)]/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className={cn("text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-primary mb-6">
            Our Creative Pathways
          </h2>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">We believe healing happens through many avenues. Our four core pillars offer diverse ways for children and caregivers to explore, express, and empower themselves.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 lg:gap-12">
          {pathways.map((pathway, i) => <div key={pathway.title} data-index={i} className={cn("transition-all duration-700 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20")} style={{
          transitionDelay: `${i * 150}ms`
        }}>
              <Card className="group h-full border-none shadow-sm hover:shadow-md bg-white rounded-[40px] transition-all duration-500 hover:scale-[1.02] overflow-hidden">
                <CardHeader className="pt-10 px-8 pb-4">
                  <div className="relative w-16 h-16 mb-6 flex items-center justify-center">
                    {}
                    <div className="absolute inset-0 bg-[var(--chart-3)] rounded-full blur-2xl opacity-0 group-hover:opacity-80 transition-opacity duration-500 scale-150" />
                    
                    {}
                    <pathway.icon className={cn("relative z-10 w-10 h-10 transition-transform duration-500 group-hover:scale-110", pathway.title === "Music" ? "text-primary" : "text-[var(--ring)]")} strokeWidth={1.5} />
                  </div>
                  <CardTitle className="font-serif text-2xl md:text-3xl text-primary">
                    {pathway.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-10">
                  <CardDescription className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed">
                    {pathway.description}
                  </CardDescription>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}